import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import InstituteSwitcher from "@/components/InstituteSwitcher";
import { slugify } from "./_shared";
import AdmissionInquiryModal from "./AdmissionInquiryModal";
import AboutMenu from "@/components/AboutMenu";
import {
  Menu,
  X,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  GraduationCap,
  ChevronDown,
  ExternalLink,
  ArrowUpRight,
} from "lucide-react";

const PolyContext = createContext(null);
export const usePoly = () => useContext(PolyContext);

// Vibrant campus palette — sapphire + amber accent + coral pop
export const PALETTE = {
  primary: "#1e3a8a", // deep sapphire blue
  primaryDark: "#172554",
  accent: "#f59e0b", // amber gold
  accentDark: "#b45309",
  pink: "#ec4899",
  teal: "#14b8a6",
  coral: "#fb7185",
  paper: "#fffdf7",
  ink: "#0f172a",
};

export const NAV = [
  { to: "", end: true, label: "Home" },
  { to: "about", label: "About" },
  { to: "departments", label: "Departments" },
  { to: "campus-engagements", label: "Campus Engagements" },
  { to: "committees", label: "Committees & Cells" },
  { to: "gallery", label: "Gallery" },
  { to: "facilities", label: "Facilities" },
  { to: "academics", label: "Academics" },
  { to: "alumni", label: "Alumni" },
  { to: "careers", label: "Careers" },
  { to: "fra-ffc", label: "FRA/FFC" },
  { to: "contact", label: "Contact" },
];

export default function PolyLayout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/website/polyaurangabad");
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Website not found");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setMobileOpen(false);
  }, [location.pathname]);

  // Admission inquiry modal — declared before any early returns to keep hook order stable
  const [inquiryOpen, setInquiryOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-slate-500 font-serif text-xl">Loading…</div>
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-slate-800">Website not found</h1>
          <p className="mt-2 text-slate-500">{err}</p>
        </div>
      </div>
    );
  }

  const inst = data.institute;
  const logoSrc = inst.logo_url || "/assets/mitcsn-logo.png";

  const linkClass = ({ isActive }) =>
    `text-sm px-3 py-2 rounded-md transition-colors whitespace-nowrap ${
      isActive
        ? "bg-amber-400 text-slate-900 font-semibold shadow-sm"
        : "text-white/90 hover:bg-white/10 hover:text-white"
    }`;
  const linkAccentClass = ({ isActive }) =>
    `text-sm px-3 py-2 rounded-md transition-colors whitespace-nowrap font-bold uppercase tracking-wider ring-2 ring-amber-300 shadow-md ${
      isActive ? "bg-amber-500 text-white" : "bg-amber-400 text-slate-900 hover:bg-amber-300"
    }`;
  // NAAC was moved to the top bar (next to Student ERP); keep this helper as
  // a passthrough so the render stays untouched.
  const buildNavPoly = () => NAV;

  return (
    <PolyContext.Provider value={data}>
      <Helmet prioritizeSeoTags>
        <title>MIT Polytechnic Chhatrapati Sambhajinagar | Diploma Engineering College in Chhatrapati Sambhajinagar</title>
        <meta
          name="title"
          content="MIT Polytechnic Chhatrapati Sambhajinagar | Diploma Engineering College in Chhatrapati Sambhajinagar"
        />
        <meta
          name="description"
          content="MIT Polytechnic Chhatrapati Sambhajinagar offers diploma engineering education with modern infrastructure, skilled faculty, industry-oriented learning, placements, sports facilities, and student development programs under G.S. Mandal."
        />
        <meta
          name="keywords"
          content="Marathwada Institute of Technology, MIT CIDCO, MIT Aurangabad, MIT Chhatrapati Sambhajinagar, GS Mandal, Engineering College Aurangabad, Engineering College Sambhajinagar, Best Engineering College in Aurangabad, Best Engineering College in Maharashtra, Engineering Admission Aurangabad, Computer Science Engineering, CSE Engineering, Computer Engineering, Information Technology, IT Engineering, Artificial Intelligence, Artificial Intelligence and Data Science, AI and DS, Data Science Engineering, Electronics Engineering, Electronics and Telecommunication Engineering, ENTC Engineering, Electrical Engineering, Mechanical Engineering, Civil Engineering, Automobile Engineering, Production Engineering, Chemical Engineering, Diploma Engineering, Polytechnic College, Polytechnic Aurangabad, Diploma in Computer Engineering, Diploma in Information Technology, Diploma in Mechanical Engineering, Diploma in Civil Engineering, Diploma in Electrical Engineering, Diploma in Electronics Engineering, Bachelor of Engineering, BE Degree, BTech Degree, Engineering Courses Maharashtra, Professional Courses, Technical Education, Engineering Programs, Undergraduate Engineering, Postgraduate Engineering, MBA, Master of Business Administration, Management Studies, Postgraduate Programs, Dr Babasaheb Ambedkar Marathwada University, DBATU, Engineering Education, Higher Education Maharashtra, Career Oriented Education, Industry Oriented Education, Placement Assistance, Campus Placement, Engineering Internship, Research and Innovation, Quest for Excellence, GS Mandal Educational Institutions, College in CIDCO, College in Aurangabad, College in Sambhajinagar, Engineering Institute Maharashtra, Technical Institute Maharashtra"
        />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="G.S. Mandal" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        <link rel="canonical" href="https://gsmandal.org/polyaurangabad" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://gsmandal.org/polyaurangabad" />
        <meta
          property="og:title"
          content="MIT Polytechnic Chhatrapati Sambhajinagar | Diploma Engineering College"
        />
        <meta
          property="og:description"
          content="Explore MIT Polytechnic Chhatrapati Sambhajinagar — quality diploma engineering education, innovation, placements, sports academy, and holistic student development."
        />
        <meta
          property="og:image"
          content="https://gsmandal.org/assets/mit-polytechnic-banner.jpg"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://gsmandal.org/polyaurangabad" />
        <meta
          name="twitter:title"
          content="MIT Polytechnic Chhatrapati Sambhajinagar | Diploma Engineering College"
        />
        <meta
          name="twitter:description"
          content="MIT Polytechnic Chhatrapati Sambhajinagar provides career-focused diploma engineering education with modern facilities and strong industry exposure."
        />
        <meta
          name="twitter:image"
          content="https://gsmandal.org/assets/mit-polytechnic-banner.jpg"
        />
      </Helmet>
      <div className="min-h-screen flex flex-col bg-slate-50">
        {/* Top contact bar */}
        <div className="bg-slate-900 text-slate-300 text-xs">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-4 flex-wrap">
              {inst.admission_phone && (
                <a
                  href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`}
                  data-testid="poly-top-phone"
                  className="inline-flex items-center gap-1 hover:text-amber-400"
                >
                  <Phone size={11} strokeWidth={1.5} className="text-amber-400" />
                  {inst.admission_phone}
                </a>
              )}
              {inst.email && (
                <a
                  href={`mailto:${inst.email}`}
                  data-testid="poly-top-email"
                  className="inline-flex items-center gap-1 hover:text-amber-400"
                >
                  <Mail size={11} strokeWidth={1.5} className="text-amber-400" />
                  {inst.email}
                </a>
              )}
            </div>
            <div className="flex items-center gap-3">
              <InstituteSwitcher currentSlug="polyaurangabad" testidPrefix="poly-top-switcher" />
              {inst.facebook_url && (
                <a href={inst.facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="poly-top-facebook" className="hover:text-amber-400">
                  <Facebook size={13} strokeWidth={1.5} />
                </a>
              )}
              {inst.instagram_url && (
                <a href={inst.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="poly-top-instagram" className="hover:text-amber-400">
                  <Instagram size={13} strokeWidth={1.5} />
                </a>
              )}
              {inst.linkedin_url && (
                <a href={inst.linkedin_url} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" data-testid="poly-top-linkedin" className="hover:text-amber-400">
                  <Linkedin size={13} strokeWidth={1.5} />
                </a>
              )}
              {inst.twitter_url && (
                <a href={inst.twitter_url} target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" data-testid="poly-top-twitter" className="hover:text-amber-400">
                  <Twitter size={13} strokeWidth={1.5} />
                </a>
              )}
              {inst.youtube_url && (
                <a href={inst.youtube_url} target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="poly-top-youtube" className="hover:text-amber-400">
                  <Youtube size={13} strokeWidth={1.5} />
                </a>
              )}
              <a
                href="https://erp.mit.asia"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="poly-top-erp"
                className="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold"
              >
                Student ERP <ArrowUpRight size={10} strokeWidth={2.5} />
              </a>
              {(data?.naac_documents || []).length > 0 && (
                <NavLink
                  to="naac"
                  data-testid="poly-top-naac"
                  className="inline-flex items-center gap-1 bg-amber-300 hover:bg-amber-200 text-slate-900 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-bold ring-1 ring-amber-400"
                >
                  NAAC
                </NavLink>
              )}
              <NavLink
                to="fees"
                data-testid="poly-top-know-fees"
                className="inline-flex items-center gap-1 bg-white hover:bg-slate-100 text-slate-900 border border-white/60 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold"
              >
                Know Your Fees
              </NavLink>
            </div>
          </div>
        </div>

        {/* Logo bar */}
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-7xl mx-auto px-4 py-3 grid grid-cols-1 md:grid-cols-[minmax(0,19fr)_minmax(0,63fr)_minmax(0,18fr)] items-center gap-4">
            <NavLink
              to=""
              end
              className="flex items-center justify-center md:justify-start"
              data-testid="poly-logo-link"
            >
              <img src={logoSrc} alt={inst.name} className="h-[96px] md:h-[112px] w-auto max-w-full object-contain" />
            </NavLink>
            <div className="text-center leading-tight">
              <div className="font-serif text-base md:text-lg text-slate-700 italic" data-testid="poly-header-trust">
                Gramaudyogik Shikshan Mandal&apos;s
              </div>
              <div className="font-serif font-bold text-xl md:text-3xl text-slate-900 uppercase mt-1" data-testid="poly-header-name">
                {inst.name}
              </div>
              {inst.city && (
                <div className="font-serif text-base md:text-lg text-slate-800 mt-1" data-testid="poly-header-city">
                  {inst.city}
                </div>
              )}
              {inst.tagline && (
                <div className="text-[10px] md:text-xs tracking-[0.3em] uppercase text-amber-600 italic mt-2">
                  {inst.tagline}
                </div>
              )}
            </div>
            <div className="hidden md:block text-right">
              {inst.code && (
                <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500" data-testid="poly-header-code">
                  <span className="text-slate-700 font-semibold">Institute Code</span> · {inst.code}
                </div>
              )}
              <div className="text-xs text-slate-600 mt-0.5">
                AICTE Approved{inst.year_of_establishment ? ` · Estd. ${inst.year_of_establishment}` : ""}
              </div>
              {inst.mandatory_disclosure_url && (
                <a
                  href={inst.mandatory_disclosure_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="poly-header-mandatory-disclosure"
                  className="mt-1 inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase font-semibold text-amber-700 hover:text-amber-900 underline underline-offset-2"
                >
                  Mandatory Disclosure
                  <ExternalLink size={10} strokeWidth={2} />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Main nav */}
        <nav
          className="sticky top-0 z-40 bg-gradient-to-r from-blue-900 via-blue-800 to-indigo-900 shadow-md"
          data-testid="poly-nav"
        >
          <div className="max-w-7xl mx-auto px-4">
            <div className="hidden xl:flex items-center gap-1 py-2 flex-wrap">
              {buildNavPoly(data).map((n) =>
                n.to === "departments" ? (
                  <DepartmentsDropdown
                    key="departments"
                    departments={data.departments || []}
                    linkClass={linkClass}
                  />
                ) : n.to === "campus-engagements" ? (
                  <CampusEngagementsDropdown
                    key="campus-engagements"
                    events={data.events || []}
                    linkClass={linkClass}
                  />
                ) : n.to === "about" ? (
                  <AboutMenu
                    key="about"
                    linkClass={linkClass}
                    testidPrefix="poly-nav-about"
                    menuClassName="bg-white text-slate-800 ring-1 ring-slate-200"
                    itemHoverClassName="hover:bg-amber-50 text-slate-800"
                  />
                ) : (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    data-testid={`poly-nav-${n.to || "home"}`}
                    className={n.highlight ? linkAccentClass : linkClass}
                  >
                    {n.label}
                  </NavLink>
                ),
              )}
            </div>
            <div className="xl:hidden flex items-center justify-between py-3">
              <span className="text-white font-medium text-sm">Menu</span>
              <button
                type="button"
                onClick={() => setMobileOpen((v) => !v)}
                className="p-2 text-white"
                aria-label="Toggle menu"
                data-testid="poly-nav-toggle"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
            {mobileOpen && (
              <div className="xl:hidden grid grid-cols-2 gap-1 pb-3">
                {buildNavPoly(data).map((n) =>
                  n.to === "departments" ? (
                    <React.Fragment key="departments">
                      <NavLink
                        to="departments"
                        onClick={() => setMobileOpen(false)}
                        className={linkClass}
                      >
                        Departments
                      </NavLink>
                      {(data.departments || []).length > 0 && (
                        <div className="col-span-2 -mt-1 mb-1 pl-4 grid grid-cols-1 gap-0.5">
                          {(data.departments || []).map((d) => (
                            <NavLink
                              key={d.id}
                              to={`departments/${slugify(d.name)}`}
                              onClick={() => setMobileOpen(false)}
                              className="text-[12px] text-white/80 hover:text-amber-300 py-1.5 border-l-2 border-white/15 pl-3"
                            >
                              {d.name}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </React.Fragment>
                  ) : n.to === "campus-engagements" ? (
                    <React.Fragment key="campus-engagements">
                      <NavLink
                        to="campus-engagements"
                        onClick={() => setMobileOpen(false)}
                        className={linkClass}
                      >
                        Campus Engagements
                      </NavLink>
                      {(() => {
                        const types = uniqueTypesWithCount(data.events || []);
                        return types.length > 0 ? (
                          <div className="col-span-2 -mt-1 mb-1 pl-4 grid grid-cols-1 gap-0.5">
                            {types.map((t) => (
                              <NavLink
                                key={t.slug}
                                to={`campus-engagements/${t.slug}`}
                                onClick={() => setMobileOpen(false)}
                                className="text-[12px] text-white/80 hover:text-amber-300 py-1.5 border-l-2 border-white/15 pl-3"
                              >
                                {t.label} ({t.count})
                              </NavLink>
                            ))}
                          </div>
                        ) : null;
                      })()}
                    </React.Fragment>
                  ) : n.to === "about" ? (
                    <AboutMenu
                      key="about"
                      linkClass={linkClass}
                      testidPrefix="poly-nav-about"
                      menuClassName="bg-white text-slate-800 ring-1 ring-slate-200"
                      itemHoverClassName="hover:bg-amber-50 text-slate-800"
                    />
                  ) : (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      onClick={() => setMobileOpen(false)}
                      className={n.highlight ? linkAccentClass : linkClass}
                    >
                      {n.label}
                    </NavLink>
                  ),
                )}
                <a
                  href="https://erp.mit.asia"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  data-testid="poly-nav-student-erp-mobile"
                  className="col-span-2 mt-2 inline-flex items-center justify-center gap-1.5 bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold text-xs uppercase tracking-wider px-3 py-2 rounded-sm shadow-sm hidden"
                >
                  Student ERP Login
                  <ExternalLink size={11} strokeWidth={2} />
                </a>
              </div>
            )}
          </div>
        </nav>

        {/* Scrolling announcement bar (institute-master driven) */}
        {Array.isArray(inst.scrollers) && inst.scrollers.length > 0 && (
          <ScrollerBar items={inst.scrollers} />
        )}

        <main className="flex-1">
          <Outlet />
        </main>

        {/* Sticky WhatsApp admission inquiry */}
        {inst.whatsapp_phone && (
          <WhatsAppFab
            phone={inst.whatsapp_phone.replace(/[^0-9]/g, "")}
            message={`Hello ${inst.name || "team"}, I have an admission inquiry.`}
          />
        )}

        {/* Admission Inquiry FAB (below WhatsApp) */}
        <AdmissionInquiryFab onClick={() => setInquiryOpen(true)} />
        <AdmissionInquiryModal
          open={inquiryOpen}
          onClose={() => setInquiryOpen(false)}
          slug={inst.website_slug}
          departments={data.departments || []}
        />

        {/* Footer */}
        <footer className="bg-slate-900 text-slate-300 mt-12">
          <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-3">
                <img src={logoSrc} alt="" className="h-10 w-10 object-contain" />
                <div className="font-serif text-xl text-white">{inst.name}</div>
              </div>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                A flagship institution of GS Mandal, established 1983. Approved by AICTE,
                offering full-time diploma programmes in engineering & technology.
              </p>
            </div>
            <div>
              <div className="text-white font-semibold mb-3">Contact</div>
              <div className="space-y-2 text-sm">
                {(inst.address1 || inst.city) && (
                  <div className="flex items-start gap-2" data-testid="poly-footer-address">
                    <MapPin size={14} strokeWidth={1.5} className="text-amber-400 mt-0.5 shrink-0" />
                    <span>
                      {[
                        inst.address1,
                        inst.address2,
                        [inst.city, inst.pincode && `– ${inst.pincode}`].filter(Boolean).join(" "),
                        inst.state,
                        inst.country || "INDIA",
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </span>
                  </div>
                )}
                {(inst.contact_phone || inst.admission_phone) && (
                  <div className="flex items-start gap-2" data-testid="poly-footer-phone">
                    <Phone size={14} strokeWidth={1.5} className="text-amber-400 mt-0.5" />
                    <span>{[inst.contact_phone, inst.admission_phone].filter(Boolean).join(" · ")}</span>
                  </div>
                )}
                {inst.email && (
                  <div className="flex items-start gap-2" data-testid="poly-footer-email">
                    <Mail size={14} strokeWidth={1.5} className="text-amber-400 mt-0.5" />
                    <a href={`mailto:${inst.email}`} className="hover:text-amber-400">{inst.email}</a>
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="text-white font-semibold mb-3">Explore</div>
              <ul className="grid grid-cols-2 gap-y-1 text-sm">
                {NAV.slice(1, 9).map((n) => (
                  <li key={n.to}>
                    <NavLink
                      to={n.to}
                      className="hover:text-amber-400 transition-colors"
                    >
                      {n.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 py-4 text-center text-xs text-slate-500">
            © {new Date().getFullYear()} {inst.name}. All rights reserved. ·{" "}
            <span className="inline-flex items-center gap-1">
              <GraduationCap size={11} strokeWidth={1.5} className="text-amber-400" />
              An institution under GS Mandal
            </span>
          </div>
        </footer>
      </div>
    </PolyContext.Provider>
  );
}


function DepartmentsDropdown({ departments, linkClass }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  const sorted = [...departments].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0)
  );

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
      data-testid="poly-nav-departments-wrap"
    >
      <NavLink
        to="departments"
        data-testid="poly-nav-departments"
        className={({ isActive }) =>
          `${linkClass({ isActive })} inline-flex items-center gap-1`
        }
        onClick={() => setOpen(false)}
      >
        Departments
        <ChevronDown
          size={13}
          strokeWidth={2}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </NavLink>
      {open && sorted.length > 0 && (
        <div
          className="absolute left-0 top-full pt-1 min-w-[260px] z-50"
          data-testid="poly-nav-departments-menu"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div className="bg-white rounded-md shadow-xl ring-1 ring-slate-200 py-2">
            {sorted.map((d) => (
              <NavLink
                key={d.id}
                to={`departments/${slugify(d.name)}`}
                data-testid={`poly-nav-dept-${d.id}`}
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-slate-700 hover:bg-amber-50 hover:text-slate-900"
              >
                <div className="font-medium">{d.name}</div>
                {d.code && (
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mt-0.5">
                    {d.code}
                  </div>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// Helper: dedupe event_types from event list, returning [{ label, slug, count }]
function uniqueTypesWithCount(events) {
  const map = new Map();
  for (const e of events) {
    const label = (e.event_type || "").trim();
    if (!label) continue;
    const slug = slugify(label);
    if (map.has(slug)) {
      map.set(slug, { ...map.get(slug), count: map.get(slug).count + 1 });
    } else {
      map.set(slug, { label, slug, count: 1 });
    }
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
}


function CampusEngagementsDropdown({ events, linkClass }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  };

  const types = uniqueTypesWithCount(events);

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
      data-testid="poly-nav-engagements-wrap"
    >
      <NavLink
        to="campus-engagements"
        data-testid="poly-nav-campus-engagements"
        className={({ isActive }) =>
          `${linkClass({ isActive })} inline-flex items-center gap-1`
        }
        onClick={() => setOpen(false)}
      >
        Campus Engagements
        {types.length > 0 && (
          <ChevronDown
            size={13}
            strokeWidth={2}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </NavLink>
      {open && types.length > 0 && (
        <div
          className="absolute left-0 top-full pt-1 min-w-[240px] z-50"
          data-testid="poly-nav-engagements-menu"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div className="bg-white rounded-md shadow-xl ring-1 ring-slate-200 py-2">
            <NavLink
              to="campus-engagements"
              data-testid="poly-nav-engagements-all"
              onClick={() => setOpen(false)}
              className="block px-4 py-2 text-sm text-slate-700 hover:bg-amber-50 hover:text-slate-900 border-b border-slate-100"
            >
              <div className="font-medium">All Stories</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
                Show every type
              </div>
            </NavLink>
            {types.map((t) => (
              <NavLink
                key={t.slug}
                to={`campus-engagements/${t.slug}`}
                data-testid={`poly-nav-engagement-type-${t.slug}`}
                onClick={() => setOpen(false)}
                className="block px-4 py-2 text-sm text-slate-700 hover:bg-amber-50 hover:text-slate-900"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium">{t.label}</span>
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">
                    {t.count}
                  </span>
                </div>
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function WhatsAppFab({ phone, message }) {
  const href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat on WhatsApp for admission inquiry"
      data-testid="poly-whatsapp-fab"
      className="fixed z-40 bottom-24 right-5 sm:bottom-28 sm:right-7 flex items-center gap-2 group"
    >
      {/* Pulse ring */}
      <span className="absolute inline-flex h-14 w-14 rounded-full bg-green-500 opacity-40 animate-ping pointer-events-none right-0 bottom-0" />
      {/* Tooltip label — hidden on small screens */}
      <span className="hidden sm:inline-flex bg-white text-slate-900 text-xs font-semibold px-3 py-2 rounded-full shadow-md ring-1 ring-slate-200 opacity-0 group-hover:opacity-100 transition-opacity">
        Admission inquiry
      </span>
      {/* Button */}
      <span className="relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white shadow-lg ring-4 ring-white transition-transform group-hover:scale-105">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="w-7 h-7"
          aria-hidden="true"
        >
          <path d="M20.52 3.48A11.78 11.78 0 0 0 12.06 0C5.5 0 .15 5.34.15 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.66a11.86 11.86 0 0 0 5.73 1.46h.01c6.56 0 11.91-5.34 11.91-11.9 0-3.18-1.24-6.17-3.45-8.42ZM12.06 21.8h-.01a9.85 9.85 0 0 1-5.03-1.38l-.36-.21-3.75.98 1-3.65-.23-.37a9.86 9.86 0 0 1-1.51-5.27c0-5.45 4.44-9.89 9.9-9.89 2.64 0 5.13 1.03 7 2.9a9.87 9.87 0 0 1 2.9 7c0 5.46-4.44 9.89-9.9 9.89Zm5.43-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.66.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.39-1.47-.88-.78-1.48-1.75-1.65-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.18-.24-.58-.48-.5-.66-.5l-.56-.01c-.2 0-.5.07-.76.37-.27.3-1.02.99-1.02 2.42 0 1.43 1.04 2.81 1.18 3 .15.2 2.06 3.15 5 4.42.7.3 1.24.48 1.66.62.7.22 1.34.19 1.84.12.56-.08 1.76-.72 2-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z" />
        </svg>
      </span>
    </a>
  );
}


function AdmissionInquiryFab({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open admission inquiry form"
      data-testid="poly-admission-inquiry-fab"
      className="fixed z-40 bottom-5 right-5 sm:bottom-7 sm:right-7 inline-flex items-center gap-2 group"
    >
      {/* Tooltip label on desktop only */}
      <span className="hidden sm:inline-flex bg-white text-slate-900 text-xs font-semibold px-3 py-2 rounded-full shadow-md ring-1 ring-slate-200 opacity-0 group-hover:opacity-100 transition-opacity">
        Contact Our Admission Team
      </span>
      <span className="relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg ring-4 ring-white transition-transform group-hover:scale-105">
        {/* Graduation cap icon (admission flavour) */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="w-7 h-7"
          aria-hidden="true"
        >
          <path d="M22 10v6" />
          <path d="M2 10l10-5 10 5-10 5z" />
          <path d="M6 12v5c3 3 9 3 12 0v-5" />
        </svg>
      </span>
    </button>
  );
}


function ScrollerBar({ items }) {
  // Render each scroller as its own clickable / plain segment so each can target its own URL.
  // We render two copies back-to-back inside a translateX(-50%) marquee for seamless looping.
  const list = items || [];
  if (list.length === 0) return null;
  const renderSegment = (s, i) => {
    const inner = (
      <span className="inline-block whitespace-nowrap px-3">
        <span className="opacity-80 mr-2 align-middle">▸</span>
        {s.text}
      </span>
    );
    if (s.url) {
      return (
        <a
          key={`${s.id}-${i}`}
          href={s.url}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline focus:underline outline-none"
          data-testid={`poly-scroller-link-${s.id}`}
        >
          {inner}
        </a>
      );
    }
    return (
      <span key={`${s.id}-${i}`} data-testid={`poly-scroller-text-${s.id}`}>
        {inner}
      </span>
    );
  };
  const duration = Math.max(20, Math.min(60, list.length * 12));
  return (
    <div
      className="bg-gradient-to-r from-amber-500 to-orange-600 text-white border-y border-amber-400/50 overflow-hidden"
      data-testid="poly-scroller-bar"
    >
      <style>{`
        @keyframes poly-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        [data-testid="poly-scroller-bar"]:hover [data-testid="poly-scroller-track"] {
          animation-play-state: paused !important;
        }
      `}</style>
      <div className="max-w-7xl mx-auto px-3 py-1.5 flex items-center gap-3">
        <span className="hidden sm:inline-flex text-[10px] tracking-[0.25em] uppercase font-bold bg-white/20 px-2 py-0.5 rounded-sm shrink-0">
          Announcement
        </span>
        <div className="flex-1 overflow-hidden">
          <span
            data-testid="poly-scroller-track"
            className="inline-block whitespace-nowrap text-sm font-medium"
            style={{ animation: `poly-marquee ${duration}s linear infinite` }}
          >
            {list.map(renderSegment)}
            {list.map((s, i) => renderSegment(s, `copy-${i}`))}
          </span>
        </div>
      </div>
    </div>
  );
}

